import { type ReactNode, useMemo } from 'react';

import type {
  CsrPlayerElement,
  MetricEvent,
} from '@spotify-internal/csr-player';
import { MetricEventName } from '@spotify-internal/csr-player';
import { Text } from '@spotify-internal/encore-web';

import { formatPlaybackTime } from '../../../helpers';
import { EventMarker } from './EventMarker';
import { SkipInactiveToggle } from './SkipInactiveToggle';
import { SpeedMenu } from './SpeedMenu';
import { TimelineSlider } from './TimelineSlider';
import { getRouteLabel, useEventMarkers } from './useEventMarkers';
import type { usePlaybackState } from './usePlaybackState';

import styles from './SimplifiedPlaybackControls.module.css';

const ROUTE_MARKER_COLOR = 'var(--essential-subdued)';

type SimplifiedPlaybackControlsProps = {
  player: CsrPlayerElement;
  metricEvents?: MetricEvent[];
  playbackState: ReturnType<typeof usePlaybackState>;
  rewindButton: ReactNode;
  forwardButton: ReactNode;
  fullscreenButton?: ReactNode;
  menuContainer?: HTMLElement;
};

export const SimplifiedPlaybackControls = ({
  player,
  metricEvents = [],
  playbackState,
  rewindButton,
  forwardButton,
  fullscreenButton,
  menuContainer,
}: SimplifiedPlaybackControlsProps) => {
  const {
    currentTimeMs,
    totalTimeMs,
    speed,
    skipInactive,
    handleSeek,
    handleSpeedChange,
    handleToggleSkipInactive,
  } = playbackState;

  const routeChangeEvents = useMemo(
    () => metricEvents.filter(e => e.name === MetricEventName.RouteChange),
    [metricEvents],
  );

  const { markers: routeMarkers, handleMarkerClick } = useEventMarkers(
    routeChangeEvents,
    player,
    {
      getColor: () => ROUTE_MARKER_COLOR,
      getLabel: getRouteLabel,
      onSeek: handleSeek,
    },
  );

  return (
    <div className={styles.root}>
      <div className={styles.timelineWrapper}>
        <TimelineSlider
          value={currentTimeMs}
          max={totalTimeMs}
          onChange={handleSeek}
        />
        {routeMarkers.length > 0 && (
          <div className={styles.markersOverlay}>
            {routeMarkers.map((marker, index) => (
              <EventMarker
                key={`${marker.name}-${marker.timestamp}-${index}`}
                marker={marker}
                onClick={handleMarkerClick}
              />
            ))}
          </div>
        )}
      </div>
      <div className={styles.controlsRow}>
        {rewindButton}
        {forwardButton}
        <Text as="span" variant="bodySmall" className={styles.time}>
          {formatPlaybackTime(currentTimeMs)} /{' '}
          {formatPlaybackTime(totalTimeMs)}
        </Text>
        <div className={styles.trailingControls}>
          <SkipInactiveToggle
            checked={skipInactive}
            onChange={handleToggleSkipInactive}
          />
          <SpeedMenu
            speed={speed}
            onSpeedChange={handleSpeedChange}
            menuContainer={menuContainer}
          />
        </div>
        {fullscreenButton}
      </div>
    </div>
  );
};
